import { createContext, useContext } from 'react';
import { ToastContainer, useToast } from '@dreamtree-org/twreact-ui';

/**
 * App-wide toasts.
 *
 * twreact-ui's `useToast` keeps its queue in local component state, so a page
 * calling `toast.success(...)` only renders anything if that same component
 * also mounts a <ToastContainer>. This provider holds ONE instance of the
 * library hook at the app root and shares it through context — composition
 * around the library component, not a re-implementation of it.
 */
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const { toast, toasts, removeToast } = useToast();
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} position="top-right" />
    </ToastContext.Provider>
  );
}

/** Returns the library's `toast` object: success / error / warning / info. */
export function useAppToast() {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  // Outside the provider (e.g. a unit test rendering a page in isolation),
  // degrade to a no-op rather than throwing and taking the page down.
  const noop = () => {};
  return { success: noop, error: noop, warning: noop, info: noop };
}
